import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { useProviderStore } from "./provider";
import router from "@/router";
import { adjustHourlyDataForTimeZone, useWeekdayFormatter } from "@/utils/helpers";
import { ubiquAxios } from "@/main";
import type { Provider, ProviderChartData } from "@/utils/types";


export const useMetricsStore = defineStore("metrics", () => {
  const queryCache = ref<Record<string, number>>({});
  const monthChartDataCache = ref<Record<string, Record<string, Record<string, number>>>>({});
  const monthlySignatures = ref<{ month: string; count: any; }[]>([]);
  const providerStore = useProviderStore();
  const monthChartData = ref<Record<string, Record<string, number>>>();
  const weekChartData = ref<Record<string, number>>();
  const hourChartData = ref<Record<string, number>>();
  const allProviderChartData = ref();
  const { formatWeekdays } = useWeekdayFormatter();

  //"2024-03" is parsed into "2024-03-01 00:00:00" by the backend
  const fetchQueryData = async (from: string, until: string) => {
    const providerStore = useProviderStore();
    const currentProvider = providerStore.currentProvider;

    if (!currentProvider) {
      console.log("No current provider found, redirecting to home page...");
      router.push("/");
      return;
    }

    const cacheKey = `${currentProvider.uuid}_${from}_${until}`;

    if (cacheKey in queryCache.value) {
      return queryCache.value[cacheKey];
    }

    try {
      const response = await ubiquAxios.post(`/log_event/count_for_rse_providers/`, {
        rse_provider_ids: [currentProvider.uuid],
        types: ["asset", "derived_asset", "ppk"],
      },
      {
        params: { created__gte: from, created__lt: until }
      });

      const data = response.data
      const total = data[0].count.asset_events_count + data[0].count.derived_asset_events_count + data[0].count.ppk_events_count
      queryCache.value[cacheKey] = total;
      return total;
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  const fetchProvidersData = async () => {
    const providers = providerStore.allProviders;
    const providerUuids = providers.map((provider: Provider) => ({
      uuid: provider.uuid,
      name: provider.name ?? "Unnamed Provider"
    }));
    if (providerUuids.length === 0) return;

    try {
      const providerPromises = providerUuids.map(async (provider) => {
        const response = await ubiquAxios.post<{ signatures: number[], months: string[] }>(`/rse_provider/${provider.uuid}/log_event/get_signatures_per_month/`)
        return {
          ...response.data,
          name: provider.name
      }
    })
      allProviderChartData.value = await Promise.all(providerPromises);
    } catch (error) {
      console.error("Error fetching data for chart: ", error)
    }
  };

  const formattedAllProviderChartData = computed(() => {
    if (!allProviderChartData.value || allProviderChartData.value.length === 0) {
      return { labels: [], datasets: [] }
    }

    const allLabelsSet = new Set<string>();

    allProviderChartData.value.forEach((providerData: ProviderChartData) => {
      Object.entries(providerData).forEach(([year, months]) => {
        if (year === "name") return;
        Object.entries(months as Record<string, number>).forEach(([month, _count]) => {
          const paddedMonth = month.padStart(2, "0");
          allLabelsSet.add(`${year}-${paddedMonth}`);
        });
      });
    });

    const allLabels = Array.from(allLabelsSet).sort((a, b) => a.localeCompare(b));
    const datasets = allProviderChartData.value.map((providerData: ProviderChartData, index: number) => {
      const providerName = providerData.name;
      const dataMap: Record<string, number> = {};

      Object.entries(providerData).forEach(([year, months]) => {
        if (year === "name") return;
        Object.entries(months as Record<string, number>).forEach(([month, count]) => {
          const paddedMonth = month.padStart(2, "0");
          dataMap[`${year}-${paddedMonth}`] = count;
        });
      });

      const data = allLabels.map(label => dataMap[label] ?? 0);

      const providerColors = [
        '#09f75b', '#cafa08', '#f4d105', '#fe9119', '#ff3d11', '#fd119d', '#c624f4', '#6a26f3', '#2186f0', '#26f2e1', '#6a245f', '#87bd23', '#f6c510', '#f8a001', '#f16b0d', '#c52c14'
      ]
      const color = providerColors[index % providerColors.length];

      return {
        label: providerName,
        data,
        tension: 0.1,
        fill: false,
        borderColor: color,
        backgroundColor: color,
        pointRadius: 2,
        pointHoverRadius: 4,
      };
    });

    return {
      labels: allLabels,
      datasets
    };
  });

  const fetchMonthChartData = async () => {
    const uuid = providerStore.currentProvider?.uuid
    if (!uuid) return;

    const cacheKey = `provider_${uuid}`;

    if (monthChartDataCache.value[cacheKey]) {
      monthChartData.value = monthChartDataCache.value[cacheKey];
      console.log("monthChartData already exists, using cache.")
      return;
    }

    try {
      const response = await ubiquAxios.post(`/rse_provider/${uuid}/log_event/get_signatures_per_month/`)
      monthChartData.value = response.data;
      monthChartDataCache.value[cacheKey] = response.data;
    } catch (error) {
      console.error("Error fetching month chart data: ", error)
    }
  };

  const formattedMonthChartData = computed(() => {
    if (!monthChartData.value) return { labels: [], values: [] }

    const formattedData: { label: string; value: number }[] = []

    Object.entries(monthChartData.value).forEach(([year, months]) => {
      Object.entries(months).forEach(([month, count]) => {
        const paddedMonth = month.padStart(2, "0")
        formattedData.push({
          label: `${year}-${paddedMonth}`,
          value: count
        })
      })
    })

    formattedData.sort((a, b) => a.label.localeCompare(b.label))
    const labels = formattedData.map(item => item.label)
    const values = formattedData.map(item => item.value)

    return { labels, values };
  });

  const fetchWeekChartData = async () => {
    if (weekChartData.value) return;

    try {
      const uuid = providerStore.currentProvider?.uuid
      const response = await ubiquAxios.post(`/rse_provider/${uuid}/log_event/get_average_signatures_per_weekday/`)
      weekChartData.value = response.data
    } catch (error) {
      console.error("Error fetching week chart data: ", error)
    }
  };

  const formattedWeekChartData = computed(() => {
    if (!weekChartData.value) return { labels: [], values: [] }

    const formattedData: { label: string; value: number }[] = []

    Object.entries(weekChartData.value).forEach(([day, count]) => {
      formattedData.push({
        label: formatWeekdays(day),
        value: count
      })
    })

    while (formattedData[0]?.label !== "Monday") {
      formattedData.push(formattedData.shift()!)
    }

    const labels = formattedData.map(item => item.label)
    const values = formattedData.map(item => item.value)

    return { labels, values }
  });

  const fetchHourChartData = async () => {
    if (hourChartData.value) return;

    try {
      const uuid = providerStore.currentProvider?.uuid
      const response = await ubiquAxios.post(`/rse_provider/${uuid}/log_event/get_average_signatures_per_hour/`)
      hourChartData.value = response.data
    } catch (error) {
      console.error("Error fetching hour data: ", error);
    }
  };

  const formattedHourChartData = computed(() => {
    if (!hourChartData.value) return { labels: [], values: [] }

    const adjustedData = adjustHourlyDataForTimeZone(hourChartData.value)
    const formattedData: { label: string; value: number }[] = []

    Object.entries(adjustedData).forEach(([hour, count]) => {
      formattedData.push({
        label: `${hour}:00`,
        value: count
      })
    })
    const labels = formattedData.map(item => item.label)
    const values = formattedData.map(item => item.value)

    return { labels, values }
  });

  const clearQueryCache = () => {
    queryCache.value = {};
    monthChartDataCache.value = {};
  };

  const clearChartData = () => {
    monthlySignatures.value = [];
    allProviderChartData.value = undefined;
    monthChartData.value = undefined;
    weekChartData.value = undefined;
    hourChartData.value = undefined;
  };

  const resetStore = () => {
    queryCache.value = {};
    monthlySignatures.value = [];
  }

  return {
    queryCache,
    allProviderChartData,
    monthlySignatures,
    monthChartDataCache,
    formattedAllProviderChartData,
    formattedMonthChartData,
    formattedWeekChartData,
    formattedHourChartData,
    fetchQueryData,
    fetchProvidersData,
    fetchMonthChartData,
    fetchWeekChartData,
    fetchHourChartData,
    clearQueryCache,
    clearChartData,
    resetStore
  };
}, { persist: true })
